/**
 * EnhancedMetricCard — single-line title, value + badge on same row.
 *
 * Layout (flex-col):
 *   ┌─────────────────────────────────┐
 *   │  ● Open Conversations           │  ← dot + title (truncate, 1 line)
 *   │  37            ↗ +5.2%          │  ← value + trend badge (same row)
 *   ├─────────────────────────────────┤
 *   │  Trending up this period  ↗     │  ← footer: trend text
 *   │  Showing trend for last 30 days │
 *   │  ∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿   │  ← sparkline
 *   └─────────────────────────────────┘
 */
import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

interface SparklineData {
  value: number;
}

interface EnhancedMetricCardProps {
  title: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  trend?: number;
  trendDirection?: 'up' | 'down' | 'flat';
  sparklineData?: SparklineData[];
  suffix?: string;
  onClick?: () => void;
}

const EnhancedMetricCard: React.FC<EnhancedMetricCardProps> = ({
  title,
  value,
  color,
  icon: _icon,
  trend,
  trendDirection = 'flat',
  sparklineData = [],
  suffix = '',
  onClick,
}) => {
  const TrendIcon = {
    up:   TrendingUp,
    down: TrendingDown,
    flat: Minus,
  }[trendDirection];

  const trendBadgeClass = {
    up:   'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    down: 'border-red-200    bg-red-50    text-red-700    dark:border-red-800    dark:bg-red-950    dark:text-red-300',
    flat: 'border-border     bg-muted     text-muted-foreground',
  }[trendDirection];

  const trendDescription = {
    up:   'Trending up this period',
    down: 'Trending down this period',
    flat: 'No significant change',
  }[trendDirection];

  return (
    <Card
      className={cn(
        '@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm transition-all duration-200 flex flex-col',
        onClick && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md',
      )}
      onClick={onClick}
    >
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2 p-6 pb-3">

        {/* Row 1: colour dot + title — always single line */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          <span className="truncate text-sm font-medium text-muted-foreground">
            {title}
          </span>
        </div>

        {/* Row 2: large value + trend badge on the same line */}
        <div className="flex items-center justify-between gap-3">
          <span className="text-2xl font-semibold tabular-nums leading-none @[250px]/card:text-3xl">
            {value.toLocaleString()}{suffix}
          </span>
          {trend !== undefined && (
            <Badge
              variant="outline"
              className={cn('shrink-0 gap-1 font-semibold', trendBadgeClass)}
            >
              <TrendIcon className="h-3 w-3" />
              {trend > 0 ? '+' : ''}
              {trend.toFixed(1)}%
            </Badge>
          )}
        </div>

      </div>

      {/* ── Footer: trend text + sparkline ───────────────────────────────── */}
      <div className="mt-auto flex flex-col items-start gap-1.5 px-6 pb-6 text-sm">
        {trend !== undefined && (
          <div className="line-clamp-1 flex items-center gap-1.5 font-medium">
            {trendDescription}
            <TrendIcon className="h-4 w-4 shrink-0" />
          </div>
        )}
        <p className="text-muted-foreground">
          {sparklineData.length >= 2
            ? 'Showing trend for the last 30 days'
            : 'Last 30 days'}
        </p>
        {sparklineData.length >= 2 && (
          <div className="h-10 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparklineData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={false}
                  animationDuration={800}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
};

export default EnhancedMetricCard;
